import React from "react";
import { PushNotificationResponse } from "apis/data-contracts";
import styled, { css } from "styled-components";
import Divider from "components/foundation/Divider";
import dayjs from "dayjs";
import {
  NOTIFICATION_DATE_FORMAT,
  PUSH_NOTIFICATION_TYPE,
  NOTIFICATION_ROUTER_PATH,
} from "config/constant";
import { useNavigate } from "react-router-dom";
import { useUpdatePushNotification } from "features/pushNotification/mutation";

export interface PushNotificationItemProps {
  pushNotificationData: PushNotificationResponse[];
}

const PushNotificationItem = ({
  pushNotificationData,
}: PushNotificationItemProps) => {
  const updatePushNotificationMutation = useUpdatePushNotification();

  const navigate = useNavigate();
  const handleNavigate = (
    id: number,
    targetUuid: string,
    type: string,
    category: string,
  ) => {
    updatePushNotificationMutation.mutate(id);

    switch (type) {
      // 공지사항 등록 -> 공지사항 상세페이지
      case PUSH_NOTIFICATION_TYPE.NOTICE_REGISTRATION:
        navigate(NOTIFICATION_ROUTER_PATH.NOTICE_REGISTRATION(targetUuid));
        break;

      // ANALYSIS 문서 1단계 결재 요청 -> 해당 문서 결재 상세페이지
      case PUSH_NOTIFICATION_TYPE.REQUEST_REVIEW:
        navigate(NOTIFICATION_ROUTER_PATH.REQUEST_REVIEW(targetUuid));
        break;

      // ANALYSIS 문서 2단계 결재 요청 -> 해당 문서 결재 상세페이지
      case PUSH_NOTIFICATION_TYPE.REQUEST_APPROVAL:
        navigate(NOTIFICATION_ROUTER_PATH.REQUEST_APPROVAL(targetUuid));
        break;

      // WORK 문서 결재 요청 -> 해당 문서 결재 상세페이지
      case PUSH_NOTIFICATION_TYPE.REQUEST_NOT_ANALYSIS:
        navigate(NOTIFICATION_ROUTER_PATH.REQUEST_NOT_ANALYSIS(targetUuid));
        break;

      // 보완 요청 -> mydesk 보완요청 필터링 리스트 보여주기
      case PUSH_NOTIFICATION_TYPE.REQ_SUPPLEMENT:
        category === "WORK"
          ? navigate(NOTIFICATION_ROUTER_PATH.REQ_SUPPLEMENT.WORK)
          : navigate(NOTIFICATION_ROUTER_PATH.REQ_SUPPLEMENT.ANALYSIS);
        break;

      // ANALYSIS 문서 1단계 승인 완료
      case PUSH_NOTIFICATION_TYPE.REVIEW:
        navigate(NOTIFICATION_ROUTER_PATH.REVIEW(targetUuid));
        break;

      // ANALYSIS 문서 2단계 승인 완료
      case PUSH_NOTIFICATION_TYPE.APPROVE:
        navigate(NOTIFICATION_ROUTER_PATH.APPROVE(targetUuid));
        break;

      // WORK 문서 승인 완료
      case PUSH_NOTIFICATION_TYPE.APPROVE_NOT_ANALYSIS:
        navigate(NOTIFICATION_ROUTER_PATH.APPROVE_NOT_ANALYSIS(targetUuid));
        break;

      // Archieve 문서 등록 -> Archives 문서 리스트 페이지
      case PUSH_NOTIFICATION_TYPE.ARCHIVES_REGISTRATION:
        navigate(NOTIFICATION_ROUTER_PATH.ARCHIVES_REGISTRATION);
        break;
    }
  };

  return (
    <PushNotificationItemWrap>
      {pushNotificationData.map((content, index) => (
        <>
          <PushNotificationItemBox
            key={content.id}
            isread={content.isRead}
            onClick={() =>
              handleNavigate(
                content.id,
                content.targetUuid,
                content.type,
                content.category,
              )
            }
          >
            <PushNotificationType>{content.title}</PushNotificationType>
            <PushNoticicationContent>{content.body}</PushNoticicationContent>
            <PushNotificationDate>
              {dayjs(content.createdAt).format(NOTIFICATION_DATE_FORMAT)}
            </PushNotificationDate>
          </PushNotificationItemBox>
          {index !== pushNotificationData.length - 1 && (
            <Divider thickness={1} length={0} />
          )}
        </>
      ))}
    </PushNotificationItemWrap>
  );
};

export default PushNotificationItem;

const PushNotificationItemWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
`;

const PushNotificationItemBox = styled.li<{
  isread: boolean;
}>`
  padding: 1.6rem;
  list-style: none;
  cursor: pointer;
  background-color: ${({ theme }) => theme.color.background.white};

  ${({ isread }) =>
    isread &&
    css`
      opacity: 0.3;
    `}
`;

const PushNotificationType = styled.div`
  ${({ theme }) => css`
    ${theme.typography.body1.semibold}
  `}
`;
const PushNoticicationContent = styled.div`
  ${({ theme }) => css`
    ${theme.typography.body1.regular}
  `}
  color: ${({ theme }) => theme.color.grey[700]};
  margin-top: 0.8rem;
  margin-bottom: 1.6rem;
`;

const PushNotificationDate = styled.div`
  ${({ theme }) => css`
    ${theme.typography.body3.regular}
  `}
  color: ${({ theme }) => theme.color.text.tertiery};
`;
